import React from "react";
import { Link } from "react-router-dom";
import {
  CLINIC, CREDENTIALS, ABOUT_SHORT, JOURNEY, APPROACH, WHY, PHILOSOPHY, FAQ, telLink, waLink,
} from "../content";
import {
  Photo, StatusPill, ConditionExplorer, VisitBlock, DoctorBlock, BookBand,
} from "../parts";
import { usePageMeta } from "../useSite";

function Home() {
  usePageMeta(
    "",
    "Physiotherapy and rehabilitation in Khopoli for every age — back, neck and joint pain, sports injuries, stroke and paralysis rehab, post-surgery recovery. Book with Dr. Yuganta Mhatre Angre (BPT, MPT)."
  );

  return (
    <>
      {/* ── Hero ── */}
      <section className="s-hero">
        <div className="container s-hero__grid">
          <div className="s-hero__copy">
            <p className="s-hero__place">
              <i className="bi bi-geo-alt-fill" aria-hidden="true" /> Physiotherapy &amp; rehabilitation in Khopoli
            </p>
            <h1 className="s-hero__title">Life is about living, not just surviving.</h1>
            <p className="s-hero__lead">
              Personalised physiotherapy to reduce pain, restore movement and get you back to the
              things you love — from a child’s first steps to staying steady in later years.
            </p>
            <div className="s-hero__actions">
              <Link to="/book" className="s-btn s-btn--primary s-btn--lg">
                <i className="bi bi-calendar2-check" aria-hidden="true" /> Book an appointment
              </Link>
              <a href={waLink()} target="_blank" rel="noreferrer" className="s-btn s-btn--wa s-btn--lg">
                <i className="bi bi-whatsapp" aria-hidden="true" /> WhatsApp us
              </a>
            </div>
            <div className="s-hero__meta">
              <StatusPill />
              <a href={telLink} className="s-hero__call">
                <i className="bi bi-telephone" aria-hidden="true" /> {CLINIC.phoneDisplay}
              </a>
            </div>
          </div>

          <figure className="s-hero__media">
            <Photo
              name="doctor-desk"
              alt="Dr. Yuganta Mhatre Angre in her consultation room at Sai Heal n Walk Physiotherapy Clinic"
              width={1400}
              height={1050}
              eager
            />
            <figcaption className="s-hero__card px-5 py-3">
              <strong>{CLINIC.doctor}</strong>
              <span>BPT, Sancheti Institute, Pune</span>
              <span>MPT, Dr. D. Y. Patil University, Pune</span>
            </figcaption>
          </figure>
        </div>
      </section>

      {/* ── Credentials ── */}
      <section className="s-creds" aria-label="Qualifications and experience">
        <div className="container s-creds__grid">
          {CREDENTIALS.map((c) => (
            <div key={c.title} className="s-creds__item">
              <i className={`bi ${c.icon}`} aria-hidden="true" />
              <div>
                <strong>{c.title}</strong>
                <span>{c.text}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── About (short) ── */}
      <section className="s-section">
        <div className="container s-split">
          <div>
            <h2 className="s-h2">Care that looks for the cause, not just the pain.</h2>
            <Link to="/about" className="s-link">
              Read more about the clinic <i className="bi bi-arrow-right-short" aria-hidden="true" />
            </Link>
          </div>
          <div className="s-prose">
            {ABOUT_SHORT.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}
          </div>
        </div>
      </section>

      {/* ── Heal. Move. Walk. (a real sequence) ── */}
      <section className="s-section s-section--tint" aria-labelledby="journey-title">
        <div className="container">
          <div className="s-head">
            <h2 id="journey-title" className="s-h2">Heal. Move. Walk.</h2>
            <p className="s-head__text">
              Recovery is a journey. With the right assessment, appropriate treatment, consistent
              exercise and your participation, meaningful improvement follows.
            </p>
          </div>
          <ol className="s-journey">
            {JOURNEY.map((j, i) => (
              <li key={j.word} className="s-journey__step">
                <span className="s-journey__num" aria-hidden="true">{i + 1}</span>
                <span className="s-journey__word">{j.word}</span>
                <h3 className="s-h3">{j.title}</h3>
                <p>{j.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── Conditions ── */}
      <section className="s-section" aria-labelledby="cond-title">
        <div className="container">
          <div className="s-head s-head--row">
            <div>
              <h2 id="cond-title" className="s-h2">Conditions we treat</h2>
              <p className="s-head__text">Kid to adult care — choose an area to see what we help with.</p>
            </div>
            <Link to="/treatments" className="s-btn s-btn--ghost">All treatments</Link>
          </div>
          <ConditionExplorer />
          <p className="s-note">
            Don’t see your problem listed?{" "}
            <a href={waLink("Hello Dr. Yuganta, I would like to ask whether physiotherapy can help with my problem.")} target="_blank" rel="noreferrer">
              Ask us on WhatsApp
            </a>{" "}
            — we’ll tell you honestly whether physiotherapy can help.
          </p>
        </div>
      </section>

      {/* ── Doctor ── */}
      <section className="s-section s-section--tint" aria-label="About Dr. Yuganta Mhatre Angre">
        <div className="container">
          <DoctorBlock />
        </div>
      </section>

      {/* ── Treatment approach ── */}
      <section className="s-section" aria-labelledby="approach-title">
        <div className="container">
          <div className="s-head">
            <h2 id="approach-title" className="s-h2">What your treatment may include</h2>
            <p className="s-head__text">
              Every plan is built around your assessment and progressed as you recover.
            </p>
          </div>
          <ul className="s-approach">
            {APPROACH.map((a) => (
              <li key={a.text}>
                <i className={`bi ${a.icon}`} aria-hidden="true" />
                {a.text}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Philosophy ── */}
      <section className="s-quote" aria-labelledby="philosophy-title">
        <div className="container s-quote__inner">
          <h2 id="philosophy-title" className="visually-hidden">Our philosophy</h2>
          <blockquote className="s-quote__text">“{CLINIC.tagline}”</blockquote>
          <div className="s-quote__body">
            {PHILOSOPHY.map((p) => <p key={p.slice(0, 20)}>{p}</p>)}
          </div>
        </div>
      </section>

      {/* ── Why choose ── */}
      <section className="s-section" aria-labelledby="why-title">
        <div className="container">
          <div className="s-head">
            <h2 id="why-title" className="s-h2">Why patients choose Sai Heal n Walk</h2>
          </div>
          <div className="s-why">
            {WHY.map((w) => (
              <div key={w.title} className="s-why__item">
                <span className="s-why__icon"><i className={`bi ${w.icon}`} aria-hidden="true" /></span>
                <h3 className="s-h3">{w.title}</h3>
                <p>{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Visit ── */}
      <section className="s-section s-section--tint" aria-labelledby="visit-title">
        <div className="container">
          <div className="s-head s-head--row">
            <div>
              <h2 id="visit-title" className="s-h2">Visit the clinic</h2>
              <p className="s-head__text">Vidyanagar, off Mogalwadi Road — opposite Tembe Swami Math.</p>
            </div>
          </div>
          <VisitBlock />
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="s-section" aria-labelledby="faq-title">
        <div className="container s-split">
          <div>
            <h2 id="faq-title" className="s-h2">Before your first visit</h2>
              <p className="s-head__text">Vidyanagar, off Mogalwadi Road — opposite Tembe Swami Math.</p>
          </div>
          <div className="s-faq">
            {FAQ.map((f, i) => (
              <details key={f.q} className="s-faq__item" open={i === 0}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <BookBand />
    </>
  );
}

export default Home;
